import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  ArrowLeft,
  Droplet,
  Zap,
  Activity,
  Cpu,
  Thermometer,
  AlertCircle,
  ShoppingCart,
  ShieldCheck,
  Mic,
  Search,
  Plus,
  Clock,
  ClipboardCheck,
  Loader2,
  Check,
  MapPin,
  Star,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../theme";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { getCurrentCoordinates } from "../lib/nativeGeolocation";
import { motion } from "framer-motion";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BottomNavigation from "../components/bottom_navigation";
import FeatureHeader from "../components/FeatureHeader";
import digestion from "../assets/icons/digestion.png";
import surgeon from "../assets/icons/surgeon.png";

import physician from "../assets/icons/physician.png";
import brain from "../assets/icons/brain.png";
import laboratory from "../assets/icons/laboratory.png"
import virus from "../assets/icons/infected.png"
import pregnancy from "../assets/icons/preg.png"
import heart from "../assets/icons/mon.png"
import coronavirus from "../assets/icons/coronavirus.png"
import glucosemeter from "../assets/icons/dia.png"
import dark from "../assets/icons/ans.png"
import blood from "../assets/icons/tes.png"
import banner1 from "../assets/banner/1.png"
import banner2 from "../assets/banner/2.png"
import banner3 from "../assets/banner/3.png"
import banner4 from "../assets/banner/4.png"

const labTrustHighlights = [
  { Icon: ShieldCheck, title: "Licensed & verified", subtitle: "Certified laboratories" },
  { Icon: ClipboardCheck, title: "Lab reviewed", subtitle: "Quality-controlled processing" },
  { Icon: Check, title: "Accurate reports", subtitle: "Validated test results" },
  { Icon: Star, title: "Top rated labs", subtitle: "Trusted by patients" },
];

export default function LabTest() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { profile } = useAuth();

  const [tests, setTests] = useState([]);
  const [pendingTests, setPendingTests] = useState([]);
  const [completedTests, setCompletedTests] = useState([]);
  const [isActiveTestsOpen, setIsActiveTestsOpen] = useState(false);
   const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [locationName, setLocationName] = useState("");
  const [loading, setLoading] = useState(true);



    const user_id = localStorage.getItem('userId');
  console.log(user_id);
   const sectionSpacing = { marginBottom: "16px" };

  const categories = [
    { name: "Blood", icon: <Droplet size={16} /> },
    { name: "Urine", icon: <Droplet size={16} /> },
    { name: "Hormone", icon: <Zap size={16} /> },
    { name: "Lipid", icon: <Activity size={16} /> },
    { name: "Diabetes", icon: <Cpu size={16} /> },
    { name: "Thyroid", icon: <Thermometer size={16} /> },
    { name: "Other", icon: <AlertCircle size={16} /> },
  ];
  

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        // Load lab tests
        const testRes = await api.get("/api/labtests");
        if (mounted && testRes?.data?.tests) {
          setTests(testRes.data.tests);
        }

        // Load user bookings only if userId is available
        if (user_id) {
          const bookingRes = await api
            .get(`/api/lab-bookings/user/${user_id}`)
            .catch(() => null);

            console.log(user_id.id);

          if (bookingRes?.data?.bookings?.length) {
            const all = bookingRes.data.bookings;
            console.log(bookingRes.data);
            setPendingTests(all);
        
          } else {
            setPendingTests([]);
            setCompletedTests([]);
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const response = await api.get("/api/providers");
        if (mounted) {
          setProviders(response?.data?.providers || []);
        }
      } catch (err) {
        console.error("Failed to fetch providers:", err);
        if (mounted) {
          setProviders([]);
        }
      } finally {
        if (mounted) {
          setLoadingProviders(false);
        }
      }
    };

    fetchProviders();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const detectLocation = async () => {
      const result = await getCurrentCoordinates();
      if (!result.ok) {
        return;
      }

      try {
        const { lat, lon } = result.coords;
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`);
        if (!res.ok) return;
        const data = await res.json();
        const addr = data.address || {};
        const detectedName =
          addr.city ||
          addr.town ||
          addr.village ||
          addr.suburb ||
          addr.neighbourhood ||
          addr.county ||
          "";
        setLocationName(detectedName);
      } catch (err) {
        console.error("Failed to detect location:", err);
      }
    };

    detectLocation();
  }, []);

  // Filtered tests
  const filteredTests = tests.filter((t) => {
    const name = t?.name?.toLowerCase() || "";
    const query = search.toLowerCase();
    return name.includes(query);
  });

  const nearbyProviders = useMemo(() => {
    const normalize = (value) => (value || "").toString().toLowerCase();
    const tokens = normalize(locationName)
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 2);

    const withScore = providers.map((provider) => {
      const haystack = normalize(`${provider?.address || ""} ${provider?.city || ""} ${provider?.name || ""}`);
      let score = 0;

      if (tokens.length > 0) {
        for (const token of tokens) {
          if (haystack.includes(token)) score += 2;
        }
      }

      if (provider?.verified) score += 1;

      return { ...provider, _nearbyScore: score };
    });

    return withScore
      .sort((a, b) => {
        if (b._nearbyScore !== a._nearbyScore) return b._nearbyScore - a._nearbyScore;
        return (a?.name || "").localeCompare(b?.name || "");
      })
      .slice(0, 4);
  }, [locationName, providers]);

 

  console.log({ pendingTests, completedTests });
  return (
    <div className="min-vh-100" style={{ backgroundColor: "var(--bg)", paddingBottom : "80px" }}>

      <Header navigate={navigate} />

      

      
    
      <main style={{ maxWidth: 640, margin: "0 auto", padding: "8px 16px 12px", boxSizing: "border-box" }}>

        <AIDoctorBanner />
  {/* 🧪 Your Active Tests */}
<div style={styles.sectionBlock}>
  <div
    style={{
      ...styles.sectionHeader,
      padding: "10px 12px",
      backgroundColor: "var(--surface)",
      borderRadius: 16,
      border: "1px solid #f0f0f0",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      cursor: "pointer",
      marginBottom: isActiveTestsOpen ? 10 : 0,
    }}
    onClick={() => setIsActiveTestsOpen((prev) => !prev)}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <h6 style={styles.sectionHeading}>Your Active Tests</h6>
      <span style={{ fontSize: 11, color: "#7b7b88", fontWeight: 600 }}>
        {pendingTests.length}
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
      <span
        style={styles.sectionLink}
        onClick={(e) => {
          e.stopPropagation();
          navigate("/active-tests");
        }}
      >
        See All
      </span>
      <ChevronRight
        size={18}
        color="var(--primary)"
        style={{
          transform: isActiveTestsOpen ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }}
      />
    </div>
  </div>

  {isActiveTestsOpen && (pendingTests.length === 0 ? (
    <p className="text-muted" style={{ marginTop: 10 }}>No active tests.</p>
  ) : (
    <div style={{
      backgroundColor: "var(--surface)",
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
    }}>
      {[...pendingTests].reverse().slice(0, 3).map((b, idx) => {
        const test = b.tests[0];
        const rawStatus = (test?.result?.status || "Processing").toLowerCase();

        // Normalise to one of three display states
        const statusKey =
          rawStatus === "completed" || rawStatus === "ready" ? "ready" :
          rawStatus === "action" || rawStatus === "action needed" || rawStatus === "action_needed" ? "action" :
          "processing";

        const statusConfig = {
          processing: {
            label: "Processing",
            badgeBg: "#FFF6DC",
            badgeColor: "#C88000",
            BadgeIcon: () => <Loader2 size={13} color="#C88000" style={{ flexShrink: 0 }} />,
            iconBg: "#FFF3DC",
            Icon: () => <Clock size={17} color="#E8A020" />,
            subLabel: "Est. results: 24–48 hrs",
            subLabelColor: "#999",
          },
          ready: {
            label: "Ready",
            badgeBg: "#E3F9EE",
            badgeColor: "#1A8A52",
            BadgeIcon: () => <Check size={13} color="#1A8A52" style={{ flexShrink: 0 }} />,
            iconBg: "#DFF5EB",
            Icon: () => <ClipboardCheck size={17} color="#1A8A52" />,
            subLabel: "Results ready",
            subLabelColor: "#1A8A52",
          },
          action: {
            label: "Action Needed",
            badgeBg: "#FDEAEA",
            badgeColor: "#C53030",
            BadgeIcon: () => <Loader2 size={13} color="#C53030" style={{ flexShrink: 0 }} />,
            iconBg: "#FDEAEA",
            Icon: () => <AlertCircle size={17} color="#C53030" />,
            subLabel: "Action required",
            subLabelColor: "#C53030",
          },
        };

        const cfg = statusConfig[statusKey];
        const bookedDate = b.createdAt
          ? new Date(b.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : null;
        const isLast = idx === Math.min(2, [...pendingTests].reverse().slice(0, 3).length - 1);

        return (
          <div
            key={b._id}
            onClick={() => navigate(`/booking-details/${b._id}`)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderBottom: isLast ? "none" : "1px solid #f4f4f6",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {/* Middle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text)", marginBottom: 1 }}>
                {test?.testId?.name || "Unnamed Test"}
              </div>
              {bookedDate && (
                <div style={{ fontSize: 10, color: "#999", marginBottom: 2 }}>
                  Booked on {bookedDate}
                </div>
              )}
              <div style={{ fontSize: 10, fontWeight: 600, color: cfg.subLabelColor }}>
                {cfg.subLabel}
              </div>
            </div>

            {/* Right: badge + price + chevron */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10,
                fontWeight: 600,
                padding: "4px 9px",
                borderRadius: 20,
                background: cfg.badgeBg,
                color: cfg.badgeColor,
                whiteSpace: "nowrap",
              }}>
                <cfg.BadgeIcon />
                {cfg.label}
              </div>
              <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text)", whiteSpace: "nowrap" }}>
                ${b.totalAmount?.toLocaleString()}
              </div>
              <svg width="6" height="10" viewBox="0 0 7 12" fill="none">
                <path d="M1 1l5 5-5 5" stroke="var(--primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        );
      })}
    </div>
  ))}
</div>



<MedicineCategories tests={tests} />

{/* Safety Banner*/}

<SafetyBanner/>

<div style={styles.sectionBlock}>
  <div style={styles.sectionHeader}>
    <h6 style={styles.sectionHeading}>Popular Tests</h6>
    <span
      style={styles.sectionLink}
      onClick={()=>navigate("/all-tests")}
    >
      See All 
    </span>
  </div>
  <div style={styles.productsRowsWrap}></div>
<div style={styles.cardsRow}>



{/*Popular TESTS */}
{filteredTests.map((test) => (
      <motion.div
        style={styles.productCard}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.95 }}
      >
  <div key={test.id} style={styles.dcard} onClick={() => navigate(`/lab-tests/${test._id}`)}>
    <div style={styles.imageWrapper}>
      <img
        src={test.image}
        alt={test.name}
        style={styles.productImage}
      />

      <div style={styles.tag}>Popular Test</div>

      <div style={styles.cartHolder}>
        <Plus size={16} color="var(--primary)" />
      </div>
    </div>

    <div style={styles.productInfo}>
      <h6 style={styles.title}>{test.name}</h6>
      <div style={styles.priceRow}>
        <span style={styles.oldPrice}>$12.50</span>
        <span style={styles.newPrice}>${test.price}</span>
      </div>
    </div>
  </div>
  </motion.div>
))}

</div>

</div>






{/* 🩺 Recommended Tests */}
<div style={styles.sectionBlock}>
  <div style={styles.sectionHeader}>
    <h6 style={styles.sectionHeading}>Recommended Tests</h6>
    <span style={styles.sectionLink} onClick={() => navigate("/all-tests")}>
      See All
    </span>
  </div>
  <div style={styles.cardsRow}>
    {[...tests].reverse().slice(0, 8).map((test) => (
      <div key={`rec-${test._id}`} style={styles.dcard} onClick={() => navigate(`/lab-tests/${test._id}`)}>
        <div style={styles.imageWrapper}>
          <img
            src={test.image}
            alt={test.name}
            style={styles.productImage}
          />
          <div style={{ ...styles.tag, backgroundColor: "rgba(16,150,100,0.75)" }}>
            Recommended
          </div>
          <div style={styles.cartHolder}>
            <Plus size={16} color="var(--primary)" />
          </div>
        </div>
        <div style={styles.productInfo}>
          <h6 style={styles.title}>{test.name}</h6>
          <div style={styles.priceRow}>
            <span style={styles.oldPrice}>$12.50</span>
            <span style={styles.newPrice}>${test.price}</span>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>

<div style={styles.sectionBlock}>
  <div style={styles.sectionHeader}>
    <h6 style={styles.sectionHeadingWithIcon}>
      <MapPin size={18} style={{ marginRight: 8 }} />
      Nearby Providers
    </h6>
    <button
      style={styles.seeAllButton}
      onClick={() => navigate('/all-providers')}
    >
      See All <ChevronRight size={16} />
    </button>
  </div>


  {loadingProviders ? (
    <p className="text-muted">Finding nearby providers...</p>
  ) : nearbyProviders.length === 0 ? (
    <p className="text-muted">No providers available right now.</p>
  ) : (
    <div style={styles.nearbyProvidersGrid}>
      {nearbyProviders.map((provider) => (
        <div
          key={provider._id}
          style={styles.nearbyProviderCard}
          onClick={() => navigate(`/providers/${provider._id}`)}
        >
          <div style={styles.nearbyProviderHero}>
            <img
              src={provider.logo || 'https://via.placeholder.com/260x150'}
              alt={provider.name}
              style={styles.nearbyProviderImage}
            />
            {provider.verified ? (
              <div style={styles.nearbyProviderVerifiedBadge}>
                <Check size={10} />
              </div>
            ) : null}
          </div>

          <h4 style={styles.nearbyProviderName}>{provider.name || 'Provider'}</h4>

          <div style={styles.nearbyProviderRatingRow}>
            <Star size={12} fill="#FFD700" color="#FFD700" />
            <span style={styles.nearbyProviderRatingText}>{provider.rating || '4.5'}</span>
            <span style={styles.nearbyProviderReviewsText}>({provider.reviews || '100'})</span>
          </div>

          <div style={styles.nearbyProviderAddress}>
            {provider.address || provider.city || 'Address not available'}
          </div>
        </div>
      ))}
    </div>
  )}
</div>

      </main>

 
    

    
    </div>
  );
}


const SafetyBanner = (() => (
  <div style={styles.safetyBanner}>
    <div style={styles.safetyContent}>
      <div style={styles.safetyIcon}>
        <ShieldCheck size={22} color="var(--primary)" />
      </div>
      <div>
        <p style={styles.safetyTitle}>Safe and verified testing</p>
        <p style={styles.safetyText}>
          with secure handling and fast results.
        </p>
      </div>
    </div>

    <button
      style={styles.learnMoreButton}
      onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
      onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
    >
      Learn More
    </button>
  </div>
));


const Header = ({ navigate, handleMouseEnter, handleMouseLeave }) => {
  const trustStripRef = useRef(null);
  const [isTrustStripPaused, setIsTrustStripPaused] = useState(false);

  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return;

    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;
      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }
      strip.scrollBy({ left: 1, behavior: "auto" });
    }, 24);

    return () => clearInterval(interval);
  }, [isTrustStripPaused]);

  return (
    <div style={{ ...styles.header, marginTop : "16px", maxWidth: 640, margin: "0 auto", padding: "0 16px", boxSizing: "border-box" }}>
      <FeatureHeader
        title="Lab Tests"
        subtitle="Book lab test from home and get result fast"
        onBack={() => navigate(-1)}
        rightSlot={(
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              style={styles.cartIcon}
              onClick={() => navigate("/lab-cart")}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <ShoppingCart size={22} color="var(--on-bg)" />
              <span style={styles.cartBadge}></span>
            </div>
          </div>
        )}
        containerStyle={{ padding: 0 }}
      />
      
   
{/* 
      <div
        ref={trustStripRef}
        style={styles.trustStrip}
        onMouseEnter={() => setIsTrustStripPaused(true)}
        onMouseLeave={() => setIsTrustStripPaused(false)}
        onTouchStart={() => setIsTrustStripPaused(true)}
        onTouchEnd={() => setIsTrustStripPaused(false)}
      >
        <div style={styles.trustTrack}>
          {[...labTrustHighlights, ...labTrustHighlights].map((item, index, list) => {
            const Icon = item.Icon;
            return (
              <React.Fragment key={`${item.title}-${index}`}>
                <div style={styles.trustItem}>
                  <div style={styles.trustItemIconWrap}>
                    <Icon size={13} color="var(--primary)" />
                  </div>
                  <div style={styles.trustItemCopy}>
                    <span style={styles.trustItemTitle}>{item.title}</span>
                    <span style={styles.trustItemSubtitle}>{item.subtitle}</span>
                  </div>
                </div>
                {index < list.length - 1 && <div style={styles.trustStripDivider}></div>}
              </React.Fragment>
            );
          })}
        </div>
      </div>
*/}
      <div style = {{marginTop : 10}}>
      <LabSearchRow />
      </div>
    </div>
  );
};


function LabSearchRow() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  const submitSearch = useCallback((value = query) => {
    const q = value.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  }, [navigate, query]);

  const handleMicClick = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { submitSearch(); return; }
    if (isListening && recognitionRef.current) { recognitionRef.current.stop(); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setIsListening(true);
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim() || '';
      if (!transcript) return;
      setQuery(transcript);
      navigate(`/search?q=${encodeURIComponent(transcript)}`);
    };
    recognitionRef.current = recognition;
    recognition.start();
  }, [isListening, navigate, submitSearch]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 64,
          borderRadius: 32,
          padding: '6px 10px',
          background: '#f7f4ff',
          border: '1px solid #ece5ff',
          boxShadow: '0 2px 8px rgba(124,77,255,0.08)',
        }}
      >
        <div
          onClick={() => navigate('/asha-cover')}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: '#efe8ff',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          <AutoAwesomeIcon style={{ fontSize: 20, color: 'var(--primary)' }} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); submitSearch(); }}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            position: 'relative',
            background: '#fff',
            border: '1px solid #eee8ff',
            borderRadius: 26,
            padding: '0 46px 0 12px',
            height: 50,
            margin: 0,
          }}
        >
          <Search size={17} color="var(--primary)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search for Lab Test..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 10,
              color: '#8a8a95',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 22,
              paddingLeft: 10,
              borderLeft: '1px solid #e6e6e8',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Mic
              size={19}
              color={isListening ? '#dc2626' : 'var(--primary)'}
              style={{ cursor: 'pointer' }}
              onClick={handleMicClick}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

function AIDoctorBanner() {
  const banners = [banner1, banner2, banner3];
  const styles = {
    bannerRow: {
      display: "flex",
      gap: 14,
      overflowX: "auto",
      overflowY: "hidden",
      paddingBottom: 6,
      WebkitOverflowScrolling: "touch",
      scrollSnapType: "x mandatory",
    },
    bannerCard: {
      flex: "0 0 100%",
      width: "100%",
      scrollSnapAlign: "start",
      borderRadius: 20,
      boxShadow: "0 8px 22px rgba(17, 24, 39, 0.08)",
      overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.7)",
    },
    bannerImage: {
      width: "100%",
      height: 170,
      objectFit: "cover",
      display: "block",
    },
  };

  return (
    <div style={styles.bannerRow}>
      {banners.map((banner, index) => (
        <motion.div
          key={banner}
          style={styles.bannerCard}
          whileHover={{ y: -3 }}
          whileTap={{ scale: 0.985 }}
        >
          <img
            src={banner}
            alt={`Lab test banner ${index + 1}`}
            style={styles.bannerImage}
            loading="lazy"
          />
        </motion.div>
      ))}
    </div>
  );
}



function MedicineCategories({ tests = [] }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const navigate = useNavigate();

  const categories = [
    "All",
    "Blood Tests",
    "Pregnancy & Fertility",
    "Infection & Fever",
    "Diabetes & Glucose",
    "Heart & Cholesterol",
    "Urine Tests",
  ];

  const filteredTests = useMemo(() => {
    if (activeCategory === "All") return tests;

    const normalizedCategory = activeCategory.toLowerCase();
    const matchMap = {
      "blood tests": ["blood", "cbc", "pcv", "full blood", "haemat", "hemat"],
      "pregnancy & fertility": ["pregnancy", "fertility", "ovulation", "hcg", "sperm"],
      "infection & fever": ["infection", "fever", "malaria", "typhoid", "hiv", "hepatitis"],
      "diabetes & glucose": ["diabetes", "glucose", "sugar", "hba1c"],
      "heart & cholesterol": ["heart", "cholesterol", "lipid", "cardiac"],
      "urine tests": ["urine", "urinalysis"],
    };

    return tests.filter((test) => {
      const rawCategory = `${test?.category || ""}`.toLowerCase();
      const rawName = `${test?.name || ""}`.toLowerCase();

      if (rawCategory.includes(normalizedCategory)) return true;

      return (matchMap[normalizedCategory] || []).some(
        (keyword) => rawCategory.includes(keyword) || rawName.includes(keyword)
      );
    });
  }, [activeCategory, tests]);

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h6 style={{ margin: 0, fontSize: 15, fontWeight: 600, textTransform: "capitalize", letterSpacing: "0.4px", color: "var(--on-bg)" }}>Lab Tests Categories</h6>
        <span
          style={{
            fontSize: 13,
            color: "#6A3AFF",
            cursor: "pointer",
            fontWeight: 600,
          }}
          onClick={() => navigate(`/all-tests?category=${encodeURIComponent(activeCategory)}`)}
        >
          See All
        </span>
      </div>

      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8, marginBottom: 16 }}>
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            onClick={() => setActiveCategory(category)}
            style={{
              flex: "0 0 auto",
              padding: "10px 16px",
              borderRadius: 999,
              border: activeCategory === category ? "1px solid var(--primary)" : "1px solid #e7e7ec",
              background: activeCategory === category ? "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)" : "#ffffff",
              color: activeCategory === category ? "#ffffff" : "#1f2937",
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              cursor: "pointer",
              boxShadow: activeCategory === category ? "0 8px 20px rgba(109,40,217,0.25)" : "none",
            }}
          >
            {category}
          </button>
        ))}
      </div>

      {filteredTests.length > 0 ? (
        <div style={styles.productsRow}>
          {filteredTests.slice(0, 8).map((test) => (
            <div key={test._id} style={styles.productCard} onClick={() => navigate(`/lab-tests/${test._id}`)}>
              <div style={styles.imageWrapper}>
                <img src={test.image} alt={test.name} style={styles.productImage} />
                <div style={styles.tag}>Lab Test</div>
                <div style={styles.cartHolder}>
                  <Plus size={16} color="var(--primary)" />
                </div>
              </div>
              <div style={styles.productInfo}>
                <h6 style={styles.title}>{test.name}</h6>
                <div style={styles.priceRow}>
                  <span style={styles.oldPrice}>$12.50</span>
                  <span style={styles.newPrice}>${test.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted" style={{ margin: 0 }}>No tests found for this category.</p>
      )}
    </div>
  );
}


const Active_Test = () => {



  return(
    <div>
      Active Test

      
    </div>
  )
}





const styles = {

 sectionBlock: {
  marginTop: 16,
 },
 sectionHeading: {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: "var(--on-bg)",
  textTransform: "capitalize",
  letterSpacing: "0.4px",
 },
 sectionHeadingWithIcon: {
  margin: 0,
  display: "flex",
  alignItems: "center",
  fontSize: 15,
  fontWeight: 600,
  color: "var(--on-bg)",
  textTransform: "capitalize",
  letterSpacing: "0.4px",
 },
 sectionLink: {
  fontSize: 13,
  color: "#6A3AFF",
  cursor: "pointer",
  fontWeight: 600,
 },
 nearbyProvidersGrid: {
  display: "flex",
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  gap: "16px",
  paddingBottom: "8px",
 },
 nearbyProviderCard: {
  flex: "0 0 80%",
  scrollSnapAlign: "start",
  borderRadius: 16,
  backgroundColor: "var(--surface)",
  overflow: "hidden",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingBottom: 12,
 },
 nearbyProviderHero: {
  width: "100%",
  height: 150,
  overflow: "hidden",
  position: "relative",
 },
 nearbyProviderImage: {
  width: "100%",
  height: "100%",
  objectFit: "cover",
 },
 nearbyProviderVerifiedBadge: {
  position: "absolute",
  top: 8,
  right: 8,
  background: "var(--primary)",
  color: "#fff",
  borderRadius: "50%",
  width: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
 },
 nearbyProviderName: {
  fontSize: 14,
  fontWeight: 600,
  marginTop: 8,
  textAlign: "center",
  color: "var(--on-bg)",
  padding: "0 12px",
 },
 nearbyProviderRatingRow: {
  display: "flex",
  alignItems: "center",
  gap: 4,
  marginTop: 4,
  color: "var(--on-bg)",
 },
 nearbyProviderRatingText: {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--on-bg)",
 },
 nearbyProviderReviewsText: {
  fontSize: 12,
  color: "var(--on-bg)",
 },
 nearbyProviderAddress: {
  marginTop: 6,
  fontSize: 11,
  color: "#6b7280",
  maxWidth: "90%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "center",
 },
  productsRowsWrap: {
    display: "grid",
    gap: "10px",
  },
  
cardsRow: {
  display: "grid",
  gridAutoFlow: "column",
  gridTemplateRows: "repeat(2, auto)",
  gridAutoColumns: "164px",
  columnGap: "12px",
  rowGap: "12px",
  overflowX: "auto",
  paddingBottom: "10px",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
},
 inlineBannerWrap: {
  marginTop: 20,
 },
 inlineBanner: {
  width: "100%",
  borderRadius: 12,
  display: "block",
 },

 productsRowsWrap: {
     display: "grid",
     gap: "10px",
   },
 productsRow: {
  display: "grid",
  gridAutoFlow: "column",
  gridTemplateRows: "repeat(2, auto)",
  gridAutoColumns: "164px",
  columnGap: "12px",
  rowGap: "12px",
  overflowX: "auto",
  paddingBottom: "10px",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
},
   emptyProductsState: {
     width: '100%',
     padding: '16px 14px',
     borderRadius: 14,
     background: '#ffffff',
     border: '1px solid #ece7f4',
     color: '#746d7e',
     fontSize: 12,
     fontWeight: 500,
   },
   productCard: {
     flex: "0 0 164px",
     minWidth: "164px",
     maxWidth: "164px",
     scrollSnapAlign: "start",
     backgroundColor: "transparent",
 
     padding: 0,
     boxShadow: "none",
     border: "none",
     cursor: "pointer",
     transition: "transform 0.2s, box-shadow 0.2s",
   },
   prescriptionBadge: {
     position: 'absolute',
     top: 8,
     left: 8,
     padding: '3px 8px',
     borderRadius: 10,
     color: '#fff',
     display : "none",
     fontSize: 9,
     fontWeight: 700,
     zIndex: 5,
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
   },
   productImageContainer: {
     position: 'relative',
     height: '200px',
     overflow: 'hidden',
     borderRadius: '18px',
     background: '#ededf0',
   },
   productImage: {
     width: '100%',
     height: '100%',
     objectFit: 'cover',
   },
   cartButton: {
     position: 'absolute',
     bottom: '8px',
     right: '8px',
     width: '38px',
     height: '38px',
     borderRadius: '50%',
     background: 'white',
     border: 'solid 1.5px var(--primary)',
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     cursor: 'pointer',
     boxShadow: '0 10px 20px rgba(0, 0, 0, 0.22)',
     color: 'var(--primary)',
   },
   productInfo: {
     marginTop : "5px",
     marginBottom: "2px",
     padding: '0 0 10',
     lineHeight: '1.3',
   },
   productName: {
     display: 'block',
     width: '100%',
     fontSize: '13px',
     fontWeight: '200',
     color: '#18141f',
     margin: '0',
     lineHeight: '1.3',
     whiteSpace: 'nowrap',
     overflow: 'hidden',
     textOverflow: 'ellipsis',
     textAlign: 'left',
   },
   productMeta: {
     fontSize: '11px',
     color: '#8f899a',
     margin: '2px 0 4px',
     whiteSpace: 'nowrap',
     overflow: 'hidden',
     textOverflow: 'ellipsis',
   },
   priceContainer: {
     display: 'flex',
     alignItems: 'center',
     gap: 6,
   },
   currentPrice: {
     fontSize: '15px',
     fontWeight: '800',
     color: '#1f1a28',
     marginLeft: 0,
   },
   originalPrice: {
     fontSize: '11px',
     marginLeft: 0,
     color: '#9b93ab',
     textDecoration: 'line-through',
   },
   buyButton: {
     width: '100%',
     padding: '4px',
     background: 'var(--primary)',
     color: 'white',
     border: 'none',
     borderRadius: '12px',
     fontSize: '14px',
     fontWeight: '600',
     cursor: 'pointer',
   },
   productsRowsWrap: {
    display: "grid",
    gap: "10px",
  },
  productsRow: {
    display: "flex",
    overflowX: "auto",
    gap: "12px",
    paddingBottom: "10px",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
  },
  emptyProductsState: {
    width: '100%',
    padding: '16px 14px',
    borderRadius: 14,
    background: '#ffffff',
    border: '1px solid #ece7f4',
    color: '#746d7e',
    fontSize: 12,
    fontWeight: 500,
  },
  productCard: {
    flex: "0 0 164px",
    minWidth: "164px",
    maxWidth: "164px",
    scrollSnapAlign: "start",
    backgroundColor: "transparent",

    padding: 0,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  prescriptionBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    padding: '3px 8px',
    borderRadius: 10,
    color: '#fff',
    display : "none",
    fontSize: 9,
    fontWeight: 700,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productImageContainer: {
    position: 'relative',
    height: '200px',
    overflow: 'hidden',
    borderRadius: '18px',
    background: '#ededf0',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cartButton: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'white',
    border: 'solid 1.5px var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.22)',
    color: 'var(--primary)',
  },
  productInfo: {
    marginTop : "5px",
    marginBottom: "2px",
    padding: '0 0 10',
    lineHeight: '1.3',
  },
  productName: {
    display: 'block',
    width: '100%',
    fontSize: '13px',
    fontWeight: '200',
    color: '#18141f',
    margin: '0',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  productMeta: {
    fontSize: '11px',
    color: '#8f899a',
    margin: '2px 0 4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  currentPrice: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1f1a28',
    marginLeft: 0,
  },
  originalPrice: {
    fontSize: '11px',
    marginLeft: 0,
    color: '#9b93ab',
    textDecoration: 'line-through',
  },
  buyButton: {
    width: '100%',
    padding: '4px',
    background: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },

 dcard: {
     flex: "0 0 164px",
    minWidth: "164px",
    maxWidth: "164px",
    scrollSnapAlign: "start",
    backgroundColor: "transparent",

    padding: 0,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
},


imageWrapper: {
   position: "relative",
    height: "200px",
    overflow: "hidden",
    borderRadius: "18px",
    background: "#ededf0",
},
productImage: {
  width: "100%",
  height: "100%",
  objectFit: "cover",
},
productInfo: {
  marginTop: "6px",
  marginBottom: "2px",
  lineHeight: "1.3",
},
addToCart: {
  marginTop: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 0,   // 👈 ensure no padding
}
,
  cartHolder: {
    position: "absolute",
    bottom: "8px",
    right: "8px",
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background: "#ffffff",
    border: "1.5px solid var(--primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.22)",
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  title: {
    fontSize: 13,
    fontWeight: 500,
    margin: 0,
    lineHeight: "1.3",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  tag: {
    position: "absolute",
    top: 8,
    left: 8,
    fontSize: 9,
    padding: "3px 8px",
    borderRadius: 10,
    backgroundColor: "rgba(106,58,255,0.70)",
    color: "#fff",
    fontWeight: 700,
    zIndex: 2,
  },

  sub: {
    fontSize: 12,
    color: "#777",
    margin: "4px 0 8px",
  },

  priceRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,

  },

  oldPrice: {
    fontSize: 11,
    color: "#9b93ab",
    textDecoration: "line-through",
  },

  newPrice: {
    fontSize: 15,
    fontWeight: 800,
    color: "#1f1a28",
  },

  bookBtn: {
    backgroundColor: "#6A3AFF",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  arrow: {
    fontSize: 18,
    lineHeight: 1,
  },

  footer: {
    fontSize: 10,
    color: "#999",
    textAlign: "center",
    marginTop: 8,
  },

 header: {
    background: "var(--bg)",
    color: "var(--on-bg)",
    position: "relative",
    width: "100%",
    overflow: "visible",
    borderBottomLeftRadius: 25,
    borderBottomRightRadius: 25,
    marginBottom: 20,
    padding: "18px 0 8px",
    fontFamily: "'Poppins', sans-serif",
  },
  headerTop: {
    display: "flex",
    marginTop: 0,
    justifyContent: "space-between",
    alignItems: "center",
  },
  backArrow: {
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--primary)",
    marginTop: 4,
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 22,
    marginLeft: 12,
    color: "var(--on-bg)",
    letterSpacing: "0.3px",
    textAlign: "center",
    lineHeight: 1.15,
  },
  cartIcon: {
    position: "relative",
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    backdropFilter: "blur(10px)",
    transition: "all 0.2s",
  },
  cartBadge: {
    position: "absolute",
    top: 8,
    right: 10,
    width: 8,
    height: 8,
    background: "var(--primary)",
    borderRadius: "50%",
    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 34,
    textAlign: "left",
  },
  trustBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    marginLeft: 34,
    marginTop: -7
  },
  trustIcon: {
    fontSize: 14,
    color: "green"
  },
  trustText: {
    fontSize: "0.85em",
    left: "-10px",
    lineHeight: "1",
  },
  trustStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    marginBottom: 10,
    marginTop: 8,
    background: "#ffffff",
    border: "1px solid #eceef1",
    borderRadius: 999,
    padding: "6px 8px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  trustTrack: {
    display: "flex",
    alignItems: "center",
    width: "max-content",
    gap: 6,
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 154,
  },
  trustItemIconWrap: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "1px solid #eceef1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  trustItemCopy: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.15,
  },
  trustItemTitle: {
    fontSize: 11,
    color: "#3D3A52",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  trustItemSubtitle: {
    fontSize: 9,
    color: "#A0A0B2",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  trustStripDivider: {
    width: 1,
    height: 20,
    background: "#d6d2e7",
    flexShrink: 0,
  },
  quickActions: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: 12,
  },
  actionCard: {
    textAlign: "center",
    border: "none",
    borderRadius: 12,
    padding: "10px 14px",
    flex: 1,
    backdropFilter: "blur(6px)",
    transition: "all 0.2s",
    cursor: "pointer",
    background: "linear-gradient(20deg, var(--surface), #7841f4)",
  },

  topRow: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
},

textBlock: {
  flex: 1,
  minWidth: 0,
},

image: {
  width: 48,
  height: 48,
  objectFit: "contain",
  borderRadius: 6,
  flexShrink: 0,
},

  safetyBanner: {
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    color: "#374151",
    marginTop: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  safetyContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  safetyIcon: {
    background: "rgba(106,58,255,0.1)",
    color: "var(--primary)",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  safetyTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
  },
  safetyText: {
    margin: 0,
    fontSize: 13,
    color: "#6B7280",
    lineHeight: 1.4,
  },
  learnMoreButton: {
    background: "var(--primary)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "opacity 0.2s",
  },

    pill: {
    fontSize: 11,
    padding: "4px 8px",
    borderRadius: 999,
    backgroundColor: "#F1F0FF",
    color: "#6A3AFF",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
   sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--on-bg)",
  },
  seeAllButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
  },

}
